import { useEffect, useRef, useState } from 'react'
import { useCatalogEvent } from 'lib/analytics/catalog'
import Linked from 'shared-components/service/Linked'
import { ResponsiveContext } from 'shared-components/contexts/ResponsiveContext'
import styles from './LabData.module.css'
import SectionHeader from './SectionHeader'
import LabCompareSearch from './LabCompareSearch'
import LabChartPanel, { labTabs, type PanelOverlay } from './LabChartPanel'
import {
  loadLabCharts,
  GEOM,
  GEOM_COMPACT,
  LAB_PEER_COLORS,
  type ChartGeometry,
  type LabChartPaths,
} from 'lib/dealhunt/audioChart'
import { HOW_WE_TEST_URL } from 'lib/dealhunt/constants'
import { brandedTitle, dealHuntProductHref } from 'lib/dealhunt/url'
import { publicConfig } from 'config'
import type { MdaqsScore } from 'lib/dealhunt/review'
import type { DealHuntLabCandidate, DealHuntLabComparison } from 'lib/dealhunt/types'
import { useDeclaredProduct } from 'lib/owner-notes/declared'
import { LAB_COMPARE_PARAM, YOURS_COLOR } from 'lib/owner-notes/curves'

/** "{brand} {name}" for a peer, matching the rest of the page; bare name until DealHunt
 *  supplies `brand` on the comparison (the slug can't yield it — brand sits at a variable depth). */
const peerTitle = (c: DealHuntLabComparison): string => brandedTitle(c.brand, c.name)

interface LabDataProps {
  /**
   * Real SoundGuys lab-measurement curves (measurement app, `loadLabCharts`). The parent only renders
   * this section when curves resolved, so at least one of the three measures is non-null.
   */
  curves: LabChartPaths
  mdaqs: MdaqsScore[]
  /** Display name of the primary product (legend / bar labels). */
  productName: string
  /** Curated comparison peers (DealHunt, category + popularity) the user can overlay. */
  comparisons: DealHuntLabComparison[]
  /** This product's DealHunt taxonomy node id — keeps it out of its own search picker. */
  nodeId?: string
}

/**
 * One overlayable product: a curated peer or a searched candidate, unified behind a
 * stable string key (NOT an array index — searched peers join and leave the list at
 * runtime, so every piece of overlay state is keyed by this).
 */
interface Peer {
  key: string
  comp: DealHuntLabComparison
  /** Searched candidates can be removed again; curated chips can only toggle. */
  removable: boolean
}

const curatedKey = (c: DealHuntLabComparison): string => `peer:${c.fullSlug}`
const candidateKey = (c: DealHuntLabCandidate): string => `cand:${c.nodeId}`

/** Hard cap on simultaneous overlays — the chart has exactly `LAB_PEER_COLORS.length` peer-line
 *  colors (same constraint as DealHunt's LAB_COMPARISON_LIMIT; raising it means a palette
 *  change here first). */
const MAX_OVERLAYS = LAB_PEER_COLORS.length

/** `blocked`: an ON attempt refused because every overlay slot is already taken. */
function compareToggleAction(isOn: boolean, slotsUsed: number): 'on' | 'off' | 'blocked' {
  if (isOn) {
    return 'off'
  }
  return slotsUsed >= MAX_OVERLAYS ? 'blocked' : 'on'
}

/** Keep a deselected line mounted this long so it can fade out before unmounting (CSS .leaving). */
const EXIT_MS = 340

/** Small headphone glyph for the mobile compare-summary eyebrow. Inline `fill:none` beats the
 *  global `svg { fill: currentColor }` rule so the outline renders hollow. */
const HeadphoneGlyph: React.VFC<{ className?: string }> = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    style={{ fill: 'none' }}
    stroke="currentColor"
    strokeWidth={1.7}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M4 13v-1a8 8 0 0 1 16 0v1" />
    <rect x="3" y="13" width="4" height="6" rx="1.5" />
    <rect x="17" y="13" width="4" height="6" rx="1.5" />
  </svg>
)

/**
 * Tabbed lab-data charts rendered from real SoundGuys measurements (`curves`, from the
 * measurement app) plus the MDAQS "Audio quality" tab (DealHunt). Users can overlay comparison peers —
 * the curated DealHunt set plus any lab-tested product found via the search picker: curves
 * are fetched on demand via `loadLabCharts`, MDAQS comes inline from DealHunt.
 */
const LabData: React.VFC<LabDataProps> = ({ curves, mdaqs, productName, comparisons, nodeId }) => {
  const { track } = useCatalogEvent()
  const tabs = labTabs(curves, mdaqs.length > 0)
  const [active, setActive] = useState(tabs[0]?.key ?? 'freq')
  const trailingSlash = publicConfig('useTrailingSlash')

  /**
   * Phone gets the taller plot frame (`GEOM_COMPACT`). Server-rendered and pre-hydration this
   * resolves to the compact frame — `ready` is false until the breakpoint listener runs, and the
   * matching `aspect-ratio` on `.chart` is the mobile one, so phones (the majority of this
   * page's traffic) paint the correct chart from the HTML with no swap. Desktop briefly draws
   * the compact frame letterboxed into its wider box before hydration corrects it: a one-frame
   * repaint inside a CSS-fixed box, never a layout shift.
   * `useOptionalContainer` because LabData is also rendered bare in the SSR spec, outside any
   * PageContainer — no provider must mean "compact", not a throw.
   */
  const responsive = ResponsiveContext.useOptionalContainer()
  const geom: ChartGeometry = responsive?.ready === true && responsive.md ? GEOM : GEOM_COMPACT

  // Mobile only: the compare list collapses to a one-line summary (first peer + "+N") that
  // taps open. Desktop ignores this — CSS at bp(md)+ always shows the chips inline.
  const [expanded, setExpanded] = useState(false)

  // Products added via the search picker — they render as removable chips after the
  // curated ones and share all overlay machinery through the same `Peer` shape.
  const [added, setAdded] = useState<DealHuntLabCandidate[]>([])

  // Comparison overlays, keyed by Peer.key. `selected` = overlays that are on; `leaving` =
  // just turned off, still fading out before unmount; `colorByKey` = the overlay color each
  // active/leaving peer holds from the shared LAB_PEER_COLORS pool (assigned on select, released
  // after the fade-out so a fading line never changes color mid-exit).
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set())
  const [leaving, setLeaving] = useState<ReadonlySet<string>>(new Set())
  const [colorByKey, setColorByKey] = useState<Record<string, string>>({})
  const [peerCurves, setPeerCurves] = useState<Record<string, LabChartPaths | null>>({})

  // Mirror of `selected` for the fade-out timeout: it must not release a color that was
  // re-selected mid-exit, and state captured in its closure would be stale by then.
  const selectedRef = useRef(selected)
  selectedRef.current = selected

  const peers: Peer[] = [
    ...comparisons.map(c => ({ key: curatedKey(c), comp: c, removable: false })),
    ...added.map(c => ({ key: candidateKey(c), comp: c, removable: true })),
  ]

  const colorOf = (key: string): string => colorByKey[key] ?? LAB_PEER_COLORS[0]

  /** `prefer` when free, else the first pool color not held by an active/leaving overlay; when
   *  all are held but one is only fading out, reuse the fading one's (a brief duplicate beats a
   *  blocked select). */
  const pickColor = (assigned: Record<string, string>, prefer?: string): string => {
    const used = new Set(Object.values(assigned))
    const free = [prefer, ...LAB_PEER_COLORS].find(c => c && !used.has(c))
    if (free) {
      return free
    }
    const fading = Object.keys(assigned).find(k => !selected.has(k))
    return fading ? assigned[fading] : LAB_PEER_COLORS[0]
  }

  // Fetch + cache one peer's curves. loadLabCharts swallows its own errors (returns null)
  // when no abort signal is passed, so no try/catch is needed here.
  const loadPeer = async (key: string, clearbuyId: number): Promise<void> => {
    const loaded = await loadLabCharts(clearbuyId)
    setPeerCurves(prev => ({ ...prev, [key]: loaded }))
  }

  // Prefetch every curated peer's curves on mount so a toggle overlays instantly (no click
  // lag). Searched candidates are fetched on pick instead — their set isn't known up front.
  useEffect(() => {
    comparisons.forEach(c => {
      const cb = c.clearbuyProductId
      if (cb !== null && cb !== undefined) {
        void loadPeer(curatedKey(c), cb)
      }
    })
    // Mount-only: the curated comparison list is fixed for the page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const toggle = (key: string, prefer?: string): void => {
    if (selected.has(key)) {
      // Deselect: keep the line mounted briefly so its fade-out can run, then drop it —
      // and only then release its color back to the pool.
      setSelected(prev => {
        const next = new Set(prev)
        next.delete(key)
        return next
      })
      setLeaving(prev => new Set(prev).add(key))
      setTimeout(() => {
        setLeaving(prev => {
          const next = new Set(prev)
          next.delete(key)
          return next
        })
        setColorByKey(prev => {
          if (selectedRef.current.has(key) || !(key in prev)) {
            return prev // re-selected mid-exit — it keeps its color
          }
          const next = { ...prev }
          delete next[key]
          return next
        })
      }, EXIT_MS)
    } else {
      if (selected.size >= MAX_OVERLAYS) {
        return // at capacity — the slot counter + picker notice explain why
      }
      setColorByKey(prev => (prev[key] ? prev : { ...prev, [key]: pickColor(prev, prefer) }))
      setSelected(prev => new Set(prev).add(key))
      // Cancel a pending fade-out if it's re-selected mid-exit.
      setLeaving(prev => {
        if (!prev.has(key)) {
          return prev
        }
        const next = new Set(prev)
        next.delete(key)
        return next
      })
    }
  }

  /**
   * A pick from the search dropdown. If the product is already a chip (curated or added) it
   * just switches that chip on — never a duplicate chip; otherwise it becomes a new removable
   * chip, selected immediately, its curves fetching in the background.
   */
  const addCandidate = (
    candidate: DealHuntLabCandidate,
    source: 'search' | 'declared' = 'search'
  ): void => {
    const existing = peers.find(p => p.comp.clearbuyProductId === candidate.clearbuyProductId)
    track('product_page_lab_compare_add', {
      lab_tab: active,
      compare_source: source,
      target_product_name: peerTitle(candidate),
      target_product_slug: candidate.fullSlug,
      action: existing ? 'already_listed' : 'added',
      slots_used: selected.size,
    })
    const prefer = source === 'declared' ? YOURS_COLOR : undefined
    if (existing) {
      if (!selected.has(existing.key)) {
        toggle(existing.key, prefer)
      }
      return
    }
    if (selected.size >= MAX_OVERLAYS) {
      return // the picker shows the at-capacity notice instead of results
    }
    const key = candidateKey(candidate)
    setAdded(prev => [...prev, candidate])
    void loadPeer(key, candidate.clearbuyProductId)
    toggle(key, prefer)
  }

  /** Remove a searched chip entirely (curves stay cached in case it's re-added). */
  const removeCandidate = (key: string): void => {
    const peer = peers.find(p => p.key === key)
    track('product_page_lab_compare_remove', {
      lab_tab: active,
      target_product_name: peer ? peerTitle(peer.comp) : '',
      target_product_slug: peer?.comp.fullSlug ?? null,
    })
    setAdded(prev => prev.filter(c => candidateKey(c) !== key))
    setSelected(prev => {
      const next = new Set(prev)
      next.delete(key)
      return next
    })
    setLeaving(prev => {
      const next = new Set(prev)
      next.delete(key)
      return next
    })
    setColorByKey(prev => {
      const next = { ...prev }
      delete next[key]
      return next
    })
  }

  // Deep link from the category page's lab dialog, "Open the full lab data"
  // (`?lab-compare=<clearbuyId>`): overlay the reader's declared pair at once, in `YOURS_COLOR`,
  // the same the category grid drew it in. Only the pair
  // the browser has declared is honoured; an arbitrary id in the URL is ignored.
  const { declared } = useDeclaredProduct()
  const deepLinked = useRef(false)
  useEffect(() => {
    if (deepLinked.current || !declared || typeof window === 'undefined') {
      return
    }
    const raw = new URLSearchParams(window.location.search).get(LAB_COMPARE_PARAM)
    const id = raw ? Number(raw) : NaN
    if (!Number.isFinite(id) || id <= 0 || declared.clearbuyProductId !== id) {
      return
    }
    deepLinked.current = true
    addCandidate(
      {
        nodeId: declared.nodeId ?? `declared:${id}`,
        name: declared.name,
        brand: null,
        brandSlug: null,
        fullSlug: declared.slug ?? '',
        imageUrl: declared.imageUrl,
        clearbuyProductId: id,
        mdaqs: null,
      },
      'declared'
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once, when the declaration hydrates
  }, [declared])

  const existingClearbuyIds: ReadonlySet<number> = new Set(
    peers
      .map(p => p.comp.clearbuyProductId)
      .filter((id): id is number => id !== null && id !== undefined)
  )

  // What the chart panel draws: every peer that is on or fading out, with whatever curves
  // have arrived (`undefined` = still loading, `null` = the lab has none; a peer with no lab
  // id never fetches, so it is `null` from the start and the legend says "no data").
  const overlays: PanelOverlay[] = peers
    .filter(p => selected.has(p.key) || leaving.has(p.key))
    .map(p => ({
      key: p.key,
      name: peerTitle(p.comp),
      color: colorOf(p.key),
      curves:
        p.comp.clearbuyProductId === null || p.comp.clearbuyProductId === undefined
          ? null
          : peerCurves[p.key],
      mdaqs: p.comp.mdaqs,
      leaving: leaving.has(p.key) && !selected.has(p.key),
    }))

  return (
    <section className={styles.section}>
      <SectionHeader
        title="Lab data"
        link={{ href: HOW_WE_TEST_URL, label: 'How we test ›' }}
        intro="Objective measurements from the SoundGuys test lab — overlay other models to see how this one compares."
      />

      <LabChartPanel
        className={styles.card}
        productName={productName}
        curves={curves}
        mdaqs={mdaqs}
        overlays={overlays}
        active={active}
        onTabClick={t => {
          track('product_page_lab_tab_click', { lab_tab: t.key, label: t.label })
          setActive(t.key)
        }}
        geom={geom}
        toolbar={
          <div className={styles.compareRow}>
            {/* Mobile-only collapsed summary: headphone eyebrow + first peer + "+N" + chevron.
                  Tapping toggles the list below. Hidden at bp(md)+ where chips show inline. */}
            <button
              type="button"
              className={styles.compareSummary}
              aria-expanded={expanded}
              onClick={() => setExpanded(v => !v)}
            >
              <HeadphoneGlyph className={styles.compareSummaryIcon} />
              <span className={styles.compareLabel}>{'Compare'}</span>
              <span className={styles.compareSummaryName}>
                {peers.length ? peerTitle(peers[0].comp) : 'Add products'}
              </span>
              {peers.length > 1 && (
                <span className={styles.compareCount}>{`+${peers.length - 1}`}</span>
              )}
              <svg
                className={styles.compareChevron}
                data-expanded={expanded}
                viewBox="0 0 12 12"
                width="12"
                height="12"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M3 4.5 6 7.5 9 4.5" />
              </svg>
            </button>

            {/* The chip list: inline-wrapped at bp(md)+, collapsible on mobile. The grid wrapper
                  animates 0fr→1fr; the inner div owns overflow + the flex layout of the chips. */}
            <div className={styles.chipList} data-expanded={expanded}>
              <div className={styles.chipListInner}>
                <span className={`${styles.compareLabel} ${styles.chipListLabel}`}>
                  {'Compare with'}
                </span>
                {peers.map(p => {
                  const isOn = selected.has(p.key)
                  return (
                    <div key={p.key} className={styles.chip} data-active={isOn}>
                      <button
                        type="button"
                        className={styles.chipToggle}
                        aria-pressed={isOn}
                        onClick={() => {
                          track('product_page_lab_compare_toggle', {
                            lab_tab: active,
                            action: compareToggleAction(isOn, selected.size),
                            compare_source: p.removable ? 'search' : 'suggested',
                            target_product_name: peerTitle(p.comp),
                            target_product_slug: p.comp.fullSlug,
                            slots_used: selected.size,
                          })
                          toggle(p.key)
                        }}
                      >
                        {p.comp.imageUrl ? (
                          // Peer's product shot; the ring tints to its overlay color when active,
                          // tying the image to its curve. Falls back to a colored dot when DealHunt
                          // ships no imageUrl for the comparison.
                          <span
                            className={styles.chipThumb}
                            style={isOn ? { borderColor: colorOf(p.key) } : undefined}
                          >
                            {/* eslint-disable-next-line @next/next/no-img-element */}
                            <img src={p.comp.imageUrl} alt="" loading="lazy" />
                          </span>
                        ) : (
                          <span
                            className={styles.chipDot}
                            style={isOn ? { backgroundColor: colorOf(p.key) } : undefined}
                          />
                        )}
                        {peerTitle(p.comp)}
                      </button>
                      {/* Candidates' fullSlug is nullable per the lab-candidates contract
                            (curated peers always carry one) — no PDP to link without it. */}
                      {Boolean(p.comp.fullSlug) && (
                        <Linked
                          href={dealHuntProductHref(
                            p.comp.fullSlug,
                            p.comp.brand,
                            trailingSlash,
                            p.comp.brandSlug
                          )}
                          className={styles.chipView}
                          aria-label={`View ${peerTitle(p.comp)}`}
                          onClick={() =>
                            track('product_page_lab_compare_view_click', {
                              lab_tab: active,
                              target_product_name: peerTitle(p.comp),
                              target_product_slug: p.comp.fullSlug,
                            })
                          }
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {'View'}
                          <svg
                            className={styles.chipViewArrow}
                            viewBox="0 0 12 12"
                            width="12"
                            height="12"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            aria-hidden="true"
                          >
                            <path d="M3.5 8.5 8.5 3.5" />
                            <path d="M4.5 3.5h4v4" />
                          </svg>
                        </Linked>
                      )}
                      {p.removable && (
                        <button
                          type="button"
                          className={styles.chipRemove}
                          aria-label={`Remove ${peerTitle(p.comp)} from the comparison`}
                          onClick={() => removeCandidate(p.key)}
                        >
                          <svg
                            viewBox="0 0 12 12"
                            width="11"
                            height="11"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.6"
                            strokeLinecap="round"
                            aria-hidden="true"
                          >
                            <path d="M3 3l6 6M9 3l-6 6" />
                          </svg>
                        </button>
                      )}
                    </div>
                  )
                })}

                <LabCompareSearch
                  excludeNodeId={nodeId}
                  existingClearbuyIds={existingClearbuyIds}
                  atCap={selected.size >= MAX_OVERLAYS}
                  onPick={addCandidate}
                />

                <span className={styles.slotCount} data-full={selected.size >= MAX_OVERLAYS}>
                  {`${selected.size} of ${MAX_OVERLAYS} overlay slots used`}
                  {selected.size >= MAX_OVERLAYS && ' — remove a product to add another'}
                </span>
              </div>
            </div>
          </div>
        }
      />
    </section>
  )
}

export default LabData
